import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { Carregando } from './componentes/ui/index.ts';
import { ProvedorSessao, useSessao } from './lib/sessao.tsx';
import type { Papel } from './lib/tipos.ts';
import { Aguardando } from './paginas/Aguardando.tsx';
import { Confirmacao } from './paginas/Confirmacao.tsx';
import { Entrar } from './paginas/Entrar.tsx';
import { Formulario } from './paginas/Formulario.tsx';
import { Inicio } from './paginas/Inicio.tsx';
import { Config } from './paginas/painel/Config.tsx';
import { Ficha } from './paginas/painel/Ficha.tsx';
import { FichaFinanceira } from './paginas/painel/FichaFinanceira.tsx';
import { Financeiro } from './paginas/painel/Financeiro.tsx';
import { Houses } from './paginas/painel/Houses.tsx';
import { Inscricoes } from './paginas/painel/Inscricoes.tsx';
import { LayoutPainel } from './paginas/painel/Layout.tsx';
import { Usuarios } from './paginas/painel/Usuarios.tsx';
import { Visao } from './paginas/painel/Visao.tsx';

/**
 * Portão do painel. Três desfechos distintos, e a diferença entre eles importa:
 * sem sessão vai para o login; com sessão mas sem aprovação vai para a tela de
 * espera (não para o login, senão a pessoa acha que errou a senha); aprovado
 * entra.
 *
 * Isto é roteamento, não segurança — cada rota da API valida o papel por conta
 * própria.
 */
function Protegida({ papeis, children }: { papeis?: Papel[]; children: React.ReactNode }) {
  const { usuario, carregando } = useSessao();

  if (carregando) return <Carregando texto="Verificando acesso" />;
  if (!usuario) return <Navigate to="/entrar" replace />;
  if (usuario.status !== 'aprovado') return <Aguardando />;

  if (papeis && (!usuario.papel || !papeis.includes(usuario.papel))) {
    // Financeiro caindo numa tela de admin vai para onde ele pode trabalhar.
    return <Navigate to="/painel/financeiro" replace />;
  }

  return <>{children}</>;
}

/** Já autenticado não precisa ver o login de novo. */
function SomenteVisitante({ children }: { children: React.ReactNode }) {
  const { usuario, carregando } = useSessao();

  if (carregando) return <Carregando />;
  if (usuario) {
    if (usuario.status !== 'aprovado') return <Aguardando />;
    return <Navigate to={usuario.papel === 'admin' ? '/painel' : '/painel/financeiro'} replace />;
  }

  return <>{children}</>;
}

export function App() {
  return (
    <Router>
      <ProvedorSessao>
        <a href="#conteudo" className="pular-para-conteudo">
          Pular para o conteúdo
        </a>

        <Routes>
          {/* público — responsável, sem login */}
          <Route path="/" element={<Inicio />} />
          <Route path="/inscricao/:token" element={<Formulario />} />
          <Route path="/confirmacao/:protocolo" element={<Confirmacao />} />

          <Route
            path="/entrar"
            element={
              <SomenteVisitante>
                <Entrar />
              </SomenteVisitante>
            }
          />

          <Route
            path="/painel"
            element={
              <Protegida>
                <LayoutPainel />
              </Protegida>
            }
          >
            {/* financeiro é o único destino comum aos dois papéis */}
            <Route path="financeiro" element={<Financeiro />} />
            <Route path="financeiro/:id" element={<FichaFinanceira />} />

            <Route
              index
              element={
                <Protegida papeis={['admin']}>
                  <Visao />
                </Protegida>
              }
            />
            <Route
              path="inscricoes"
              element={
                <Protegida papeis={['admin']}>
                  <Inscricoes />
                </Protegida>
              }
            />
            <Route
              path="inscricoes/:id"
              element={
                <Protegida papeis={['admin']}>
                  <Ficha />
                </Protegida>
              }
            />
            <Route
              path="houses"
              element={
                <Protegida papeis={['admin']}>
                  <Houses />
                </Protegida>
              }
            />
            <Route
              path="usuarios"
              element={
                <Protegida papeis={['admin']}>
                  <Usuarios />
                </Protegida>
              }
            />
            <Route
              path="config"
              element={
                <Protegida papeis={['admin']}>
                  <Config />
                </Protegida>
              }
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ProvedorSessao>
    </Router>
  );
}
